import { APIURL } from "./config";

// ── Framework ───────────────────────────────────────────────────────────────

export type SignalPolarity = "Positive" | "Concern";

export interface CompetencySignal {
  id: string;
  text: string;
  polarity: SignalPolarity;
  displayOrder: number;
}

export interface CompetencyLevelAnchor {
  level: number;
  description: string;
}

export interface Competency {
  id: string;
  key: string;
  name: string;
  definition: string;
  whatWeMeasure: string;
  weight: number;
  minimumLevel: number | null;
  isObservationOnly: boolean;
  displayOrder: number;
  levelAnchors: CompetencyLevelAnchor[];
  signals: CompetencySignal[];
}

// ── Evaluation ──────────────────────────────────────────────────────────────

export type EvidenceConfidence = "Low" | "Medium" | "High";

export type InterviewRecommendation =
  | "StrongHire"
  | "Hire"
  | "LeanHire"
  | "LeanNoHire"
  | "NoHire";

export interface CompetencyScore {
  competencyId: string;
  competencyKey: string;
  competencyName: string;
  level: number | null;
  notObservedReason: string | null;
  suggestedLevel: number | null;
  deviationReason: string | null;
  confidence: EvidenceConfidence;
  evidenceText: string | null;
  signalIds: string[];
}

export interface InterviewEvaluation {
  id: string;
  candidateId: string;
  roleId: string;
  roleStatusId: string;
  evaluatorId: string;
  evaluatorName: string | null;
  status: "Draft" | "Submitted";
  weightedScore: number | null;
  normalisedScore: number | null;
  recommendation: InterviewRecommendation | null;
  gateBreached: boolean;
  overallComments: string | null;
  createdAt: string;
  updatedAt: string | null;
  submittedAt: string | null;
  competencyScores: CompetencyScore[];
}

export interface StageEvaluationSummary {
  candidateId: string;
  roleId: string;
  roleStatusId: string;
  submittedCount: number;
  combinedNormalisedScore: number | null;
  combinedRecommendation: InterviewRecommendation | null;
  gateBreached: boolean;
  gateBreachReasons: string[];
  evaluations: InterviewEvaluation[];
}

export interface SaveCompetencyScore {
  competencyId: string;
  level: number | null;
  notObservedReason?: string | null;
  suggestedLevel?: number | null;
  deviationReason?: string | null;
  confidence: EvidenceConfidence;
  evidenceText?: string | null;
  signalIds: string[];
}

export interface SaveEvaluationRequest {
  candidateId: string;
  roleId: string;
  roleStatusId: string;
  overallComments?: string | null;
  competencyScores: SaveCompetencyScore[];
}

/** Thrown by submit() so the UI can render the per-competency validation list. */
export class EvaluationValidationError extends Error {
  problems: string[];
  constructor(message: string, problems: string[]) {
    super(message);
    this.name = "EvaluationValidationError";
    this.problems = problems;
  }
}

const authHeaders = (token: string) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

/**
 * The structured-evaluation endpoints 404 when Interviews:StructuredEvaluationEnabled
 * is off. Callers treat null as "feature not switched on" and fall back to the
 * existing grading UI rather than showing an error.
 */
export const GetCompetencies = async (token: string): Promise<Competency[] | null> => {
  const res = await fetch(`${APIURL}/InterviewEvaluation/competencies`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 404) return null;
  if (!res.ok) throw new Error("Failed to load the competency framework");

  return res.json();
};

export const GetMyEvaluation = async (
  candidateId: string,
  roleId: string,
  roleStatusId: string,
  token: string
): Promise<InterviewEvaluation | null> => {
  const params = new URLSearchParams({ candidateId, roleId, roleStatusId });
  const res = await fetch(`${APIURL}/InterviewEvaluation/mine?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 404) return null;
  if (!res.ok) throw new Error("Failed to load your evaluation");

  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

export const GetStageEvaluations = async (
  candidateId: string,
  roleId: string,
  roleStatusId: string,
  token: string
): Promise<StageEvaluationSummary | null> => {
  const params = new URLSearchParams({ candidateId, roleId, roleStatusId });
  const res = await fetch(`${APIURL}/InterviewEvaluation/stage?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 404) return null;
  if (!res.ok) throw new Error("Failed to load evaluations for this stage");

  return res.json();
};

export const SaveEvaluationDraft = async (
  request: SaveEvaluationRequest,
  token: string
): Promise<InterviewEvaluation> => {
  const res = await fetch(`${APIURL}/InterviewEvaluation/draft`, {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify(request),
  });

  if (!res.ok) throw new Error((await res.text()) || "Failed to save draft");

  return res.json();
};

export const SubmitEvaluation = async (
  request: SaveEvaluationRequest,
  token: string
): Promise<InterviewEvaluation> => {
  const res = await fetch(`${APIURL}/InterviewEvaluation/submit`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(request),
  });

  if (!res.ok) {
    const raw = await res.text();

    try {
      const parsed = JSON.parse(raw);
      if (parsed?.problems?.length) {
        throw new EvaluationValidationError(
          parsed.message ?? "Evaluation is incomplete.",
          parsed.problems
        );
      }
      throw new Error(parsed?.message ?? raw);
    } catch (e) {
      if (e instanceof EvaluationValidationError) throw e;
      throw new Error(raw || "Failed to submit evaluation");
    }
  }

  return res.json();
};

// ── Transcript auto-fill (advisory only) ────────────────────────────────────

export interface TranscriptCompetencyDraft {
  competencyId: string;
  competencyKey: string;
  suggestedLevel: number | null;
  signalIds: string[];
  evidenceText: string;
  confidence: EvidenceConfidence;
}

export interface TranscriptDraft {
  competencies: TranscriptCompetencyDraft[];
}

export interface ImportTranscriptRequest {
  candidateId: string;
  roleId: string;
  roleStatusId: string;
  transcriptText: string;
}

/**
 * Returns null when the transcript-autofill feature is off (404), so callers can hide
 * the "auto-fill" affordance rather than showing an error for something not enabled.
 */
export const DraftFromTranscript = async (
  request: ImportTranscriptRequest,
  token: string
): Promise<TranscriptDraft | null> => {
  const res = await fetch(`${APIURL}/InterviewEvaluation/draft-from-transcript`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(request),
  });

  if (res.status === 404) return null;
  if (!res.ok) throw new Error((await res.text()) || "Failed to generate a draft from this transcript");

  return res.json();
};

// ── Display helpers ─────────────────────────────────────────────────────────

export const RECOMMENDATION_LABELS: Record<InterviewRecommendation, string> = {
  StrongHire: "Strong Hire",
  Hire: "Hire",
  LeanHire: "Lean Hire",
  LeanNoHire: "Lean No Hire",
  NoHire: "No Hire",
};

export const RECOMMENDATION_STYLES: Record<InterviewRecommendation, string> = {
  StrongHire: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  Hire: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  LeanHire: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  LeanNoHire: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  NoHire: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

/**
 * Mirrors InterviewScoringService.SuggestLevel on the server so the interviewer sees
 * the suggestion update as they tick signals, without a round trip. The server
 * recomputes on submit; this is a hint, never the stored value on its own.
 */
export const suggestLevel = (
  positiveCount: number,
  concernCount: number,
  totalPositiveAvailable: number
): number | null => {
  if (positiveCount === 0 && concernCount === 0) return null;
  if (concernCount >= 3 && positiveCount === 0) return 1;
  if (concernCount > positiveCount) return 2;

  if (totalPositiveAvailable > 0) {
    const ratio = positiveCount / totalPositiveAvailable;
    if (ratio >= 0.75 && concernCount === 0) return 5;
    if (ratio >= 0.5) return 4;
  }

  return 3;
};
